import React, { useState, useEffect, useCallback, useRef } from 'react';
import RecordsInlineToolbar, { RecordsPageHeader, RecordsBreadcrumb } from './RecordsToolbar';
import RecordsTable from './RecordsTable';
import RecordsPagination from './RecordsPagination';
import UploadFile from '../Surveys/UploadFile';
import ModalQr from '../Surveys/ModalQr';
import ModalEmailConfirmations from '../Surveys/ModalEmailConfirmations';
import FormUpgradePlan from '../../settings/Users/FormUpgradePlan';
import ExcelColumnSelector from './ExcelColumnSelector';
import FormatCalendar from '../Calendar/FormatCalendar';
import ModalMatrixReport from './ModalMatrixReport';

const TOKEN = document.querySelector("[name='csrf-token']")?.content;

export default function RecordsContainer({
  formato, format_category, users, procesos, positions,
  domain, estados, custom_view_id, custom_view_name, survey_parent_id,
  view, count_registers, current_plan, breadcrumb,
  tenant, configuration_format, format_pdfs, matrix_reports,
  format_custom_view_dashboard, plans, state_records,
  survey_parent_token, key_text, imageUpload, format_calendar_enabled,
  format_calendar_default_view
}) {
  // Modo de vista: tabla (lista) o calendario. Arranca en calendario solo si el
  // formato lo tiene habilitado y configurado como vista por defecto.
  const [viewMode, setViewMode] = useState(
    format_calendar_enabled && format_calendar_default_view === 'calendar' ? 'calendar' : 'table'
  );
  // Table state
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 30 });
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(configuration_format?.quantity_to_show || 30);
  const [sortField, setSortField] = useState(formato.format_sort_by || 'default');
  const [sortDir, setSortDir] = useState((formato.format_sort_order || '').toLowerCase());
  const [filters, setFilters] = useState({});
  const [pendingFilters, setPendingFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [matrixReport, setMatrixReport] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [searchColumn, setSearchColumn] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedSearchColumn, setAppliedSearchColumn] = useState('');

  // Modal state
  const [showColumnOptions, setShowColumnOptions] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showUpgradePlanModal, setShowUpgradePlanModal] = useState(false);
  const [showEmailConfirmationsModal, setShowEmailConfirmationsModal] = useState(false);
  const [selectedSurveyIdForConfirmations, setSelectedSurveyIdForConfirmations] = useState(null);
  const [showExcelColumnSelector, setShowExcelColumnSelector] = useState(false);

  // Progress/banner state
  const [isUpdatingAll, setIsUpdatingAll] = useState(formato.is_updating_all || false);
  const [updateTime, setUpdateTime] = useState(0);
  const [updateHasError, setUpdateHasError] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(formato.is_duplicating || false);
  const [duplicateHasError, setDuplicateHasError] = useState(false);
  const [creatingExcel, setCreatingExcel] = useState(formato.creating_excel || false);
  const [excelWasCreated, setExcelWasCreated] = useState(false);
  const [lastExcelUrl, setLastExcelUrl] = useState(formato.last_excel_url || '');
  const [creatingTxt, setCreatingTxt] = useState(formato.creating_txt || false);
  const [txtWasCreated, setTxtWasCreated] = useState(false);
  const [lastTxtUrl, setLastTxtUrl] = useState(formato.last_txt_url || '');
  const [txtExportType, setTxtExportType] = useState('txt');
  const [statePDF, setStatePDF] = useState('');
  const [creatingPdf, setCreatingPdf] = useState(formato.creating_pdf || false);
  const [pdfWasCreated, setPdfWasCreated] = useState(false);
  const [lastPdfUrl, setLastPdfUrl] = useState(formato.last_pdf_url || '');
  const [msgUpload, setMsgUpload] = useState('');
  const [msgUploadShow, setMsgUploadShow] = useState(false);
  const [planLimitMsg, setPlanLimitMsg] = useState('');

  // Taimes integration
  const [hasTaimesIntegration, setHasTaimesIntegration] = useState(false);
  const [taimesLoading, setTaimesLoading] = useState(false);
  const taimesFileInput = useRef(null);

  // Refs for polling
  const updatePollRef = useRef(null);
  const updateTimerRef = useRef(null);
  const excelPollRef = useRef(null);
  const txtPollRef = useRef(null);
  const pdfPollRef = useRef(null);
  const duplicatePollRef = useRef(null);
  const cursorRef = useRef({ lastId: null, lastSurveyCount: null });
  const prevPageRef = useRef(1);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (updatePollRef.current) clearInterval(updatePollRef.current);
      if (updateTimerRef.current) clearInterval(updateTimerRef.current);
      if (excelPollRef.current) clearInterval(excelPollRef.current);
      if (txtPollRef.current) clearInterval(txtPollRef.current);
      if (duplicatePollRef.current) clearInterval(duplicatePollRef.current);
    };
  }, []);

  // Start update polling if already updating
  useEffect(() => {
    if (isUpdatingAll) startUpdatePolling();
  }, []);

  // Check Taimes integration
  useEffect(() => {
    fetch(`/formatos/get_taimes_integration/${formato.id}`, {
      headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' }
    })
    .then(res => res.json())
    .then(data => {
      if (data.taimes_integration && data.taimes_integration.schema_id && data.taimes_integration.field_mapping?.mappings?.length > 0) {
        setHasTaimesIntegration(true);
      }
    })
    .catch(() => {});
  }, []);

  // Start excel polling if already creating
  useEffect(() => {
    if (creatingExcel) startExcelPolling();
  }, []);

  // Start txt polling if already creating
  useEffect(() => {
    if (creatingTxt) startTxtPolling();
  }, []);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
        sort_field: sortField,
      });
      // Cuando no hay columna elegida (default) no mandamos direccion: el backend
      // resuelve DESC (mas reciente primero). Asi evitamos el 'asc' viejo que
      // quedaba persistido en format_sort_order.
      if (sortDir && sortField !== 'default') params.append('sort_dir', sortDir);

      if (view) params.append('view', view);
      if (custom_view_id) params.append('custom_view_id', custom_view_id);
      if (survey_parent_id && survey_parent_id != 0) params.append('survey_parent_id', survey_parent_id);
      if (appliedSearch) {
        params.append('search', appliedSearch);
        if (appliedSearchColumn) params.append('search_column', appliedSearchColumn);
      }

      const isNextPage = page === prevPageRef.current + 1;
      if (sortField === 'default' && isNextPage && cursorRef.current.lastId) {
        params.append('after_id', cursorRef.current.lastId);
      }

      Object.entries(filters).forEach(([qid, f]) => {
        if (f.value) {
          params.append(`filter[${qid}]`, f.value);
          if (f.operator) params.append(`op[${qid}]`, f.operator);
        }
      });

      const res = await fetch(
        `/formatos/records/${formato.id}/data?${params.toString()}`,
        { headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' } }
      );
      // Sin "Ingresar" el backend responde 403 con un cuerpo sin columns/rows/meta.
      // Guardarlos en undefined tumbaba el render entero (pantalla en blanco).
      if (res.status === 403) {
        setAccessDenied(true);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const json = await res.json();
      setAccessDenied(false);
      setColumns(json.columns);
      setRows(json.rows);
      setMeta(json.meta);

      cursorRef.current = {
        lastId: json.meta.last_id,
        lastSurveyCount: json.meta.last_survey_count
      };
      prevPageRef.current = page;
    } catch (err) {
      console.error('Records fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [formato.id, page, perPage, sortField, sortDir, filters, view, custom_view_id, survey_parent_id, appliedSearch, appliedSearchColumn]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // =========== Sorting, Filtering, Selection ===========

  const persistSort = (field, dir) => {
    fetch(`/formatos/sort_list/${formato.id}/${field}/${dir || 'ASC'}`, {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' }
    }).catch(() => {});
  };

  const handleSort = useCallback((fieldId, forceDir) => {
    const fieldStr = fieldId.toString();
    let newField = fieldStr;
    let newDir = '';
    if (forceDir) {
      newField = fieldStr;
      newDir = forceDir;
    } else if (sortField === fieldStr) {
      // Cycle: asc → desc → default
      if (sortDir === 'asc') {
        newDir = 'desc';
      } else if (sortDir === 'desc') {
        newField = 'default';
        newDir = '';
      } else {
        newDir = 'asc';
      }
    } else {
      newDir = 'asc';
    }
    setSortField(newField);
    setSortDir(newDir);
    setPage(1);
    cursorRef.current = { lastId: null, lastSurveyCount: null };
    persistSort(newField, newField === 'default' ? 'DESC' : (newDir.toUpperCase() || 'ASC'));
  }, [sortField, sortDir, formato.id]);

  const handlePendingFilterChange = useCallback((questionId, value, operator) => {
    setPendingFilters(prev => ({
      ...prev,
      [questionId]: { value, operator: operator || 'contiene' }
    }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setFilters({ ...pendingFilters });
    setPage(1);
    cursorRef.current = { lastId: null, lastSurveyCount: null };
  }, [pendingFilters]);

  const handleClearFilters = useCallback(() => {
    setPendingFilters({});
    setFilters({});
    setSearchText('');
    setSearchColumn('');
    setAppliedSearch('');
    setAppliedSearchColumn('');
    setPage(1);
    cursorRef.current = { lastId: null, lastSurveyCount: null };
  }, []);

  const handleCancelFilters = useCallback(() => {
    setPendingFilters({});
    setFilters({});
    setShowFilters(false);
    setPage(1);
    cursorRef.current = { lastId: null, lastSurveyCount: null };
  }, []);

  const handleSelect = useCallback((surveyId) => {
    setSelectedIds(prev =>
      prev.includes(surveyId)
        ? prev.filter(id => id !== surveyId)
        : [...prev, surveyId]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.length === rows.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(rows.map(r => r.id));
    }
  }, [rows, selectedIds.length]);

  const [savingCell, setSavingCell] = useState(null);

  const handleCellSave = useCallback(async (surveyId, questionId, value, selectItems = []) => {
    const bulkIds = selectedIds.length > 0 ? selectedIds : [];
    const isBulk = bulkIds.length > 0;

    if (isBulk) {
      if (!confirm(`¿Editar este campo en ${bulkIds.length} registro(s) seleccionados?`)) return;
    }

    setEditingCell(null);
    setSavingCell({ surveyId, questionId });
    try {
      const body = { value, selectItems: isBulk ? bulkIds : [] };
      const res = await fetch(
        `/formatos/update_survey_values/${surveyId}/${questionId}`,
        {
          method: 'PATCH',
          headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      );
      if (res.ok) {
        await fetchData(true);
        if (isBulk) setSelectedIds([]);
      }
    } catch (err) {
      console.error('Cell save error:', err);
    } finally {
      setSavingCell(null);
    }
  }, [fetchData, selectedIds]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`¿Eliminar ${selectedIds.length} registro(s)?`)) return;

    try {
      const res = await fetch(
        `/formatos/delete_surveys/${selectedIds.join(',')}`,
        {
          method: 'DELETE',
          headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' }
        }
      );
      if (res.ok) {
        setSelectedIds([]);
        fetchData();
      }
    } catch (err) {
      console.error('Bulk delete error:', err);
    }
  }, [selectedIds, fetchData]);

  const handleBulkViewChange = useCallback(async (viewState) => {
    if (selectedIds.length === 0) return;
    try {
      const res = await fetch(
        `/formatos/update_suveys_view/${selectedIds.join(',')}/${viewState}`,
        {
          method: 'PATCH',
          headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' }
        }
      );
      if (res.ok) {
        setSelectedIds([]);
        fetchData();
      }
    } catch (err) {
      console.error('Bulk view change error:', err);
    }
  }, [selectedIds, fetchData]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  // =========== Copy Link ===========

  const handleCopyLink = useCallback(() => {
    if (current_plan && (meta.total || count_registers) >= current_plan.records_plan) {
      setPlanLimitMsg('Has alcanzado el límite de registros de tu plan.');
      setShowUpgradePlanModal(true);
      return;
    }
    let url;
    if (survey_parent_id && parseInt(survey_parent_id) > 0 && survey_parent_token) {
      url = `${domain}/formatos/generate_questions_surveys/${formato.token}/${survey_parent_token}/parent`;
    } else {
      url = `${domain}/formatos/generate_questions/${formato.token}/0/${custom_view_id && parseInt(custom_view_id) > 0 ? custom_view_id : 0}`;
    }
    navigator.clipboard.writeText(url).then(() => {
      setPlanLimitMsg('Link copiado al portapapeles');
      setTimeout(() => setPlanLimitMsg(''), 3000);
    });
  }, [domain, formato.token, survey_parent_id, survey_parent_token, current_plan, meta.total, count_registers]);

  // =========== Export (Excel / CSV / TXT) ===========

  const buildExportParams = useCallback((columnIds, includeHeader) => {
    const urlParams = new URLSearchParams();
    if (view) urlParams.append('view', view);
    if (columnIds && columnIds.length > 0) urlParams.append('column_ids', columnIds.join(','));
    if (includeHeader === false) urlParams.append('no_header', '1');
    const opMap = { mayor_igual: 'mayorigual', menor_igual: 'menorigual' };
    Object.entries(filters).forEach(([qid, f]) => {
      if (f && f.value) {
        const op = opMap[f.operator] || f.operator || 'contiene';
        urlParams.append(qid, `${f.value}&${op}`);
      }
    });
    return urlParams.toString();
  }, [view, filters]);

  const handleExportFromSelector = useCallback(async ({ columnIds, format: exportFormat, includeHeader }) => {
    setShowExcelColumnSelector(false);
    const queryStr = buildExportParams(columnIds, includeHeader);

    if (exportFormat === 'excel') {
      setCreatingExcel(true); setExcelWasCreated(false); setLastExcelUrl('');
      try {
        await fetch(`/formatos/format_categories/${format_category.id}/formats/${formato.id}/get_surveys_index_excel_view/${custom_view_id || 0}/${survey_parent_id || 0}/download${queryStr ? '?' + queryStr : ''}`, {
          headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' }
        });
        startExcelPolling();
      } catch (err) { console.error('Export Excel error:', err); setCreatingExcel(false); }
    } else if (exportFormat === 'txt') {
      setTxtExportType('txt'); setCreatingTxt(true); setTxtWasCreated(false); setLastTxtUrl('');
      try {
        await fetch(`/formatos/format_categories/${format_category.id}/formats/${formato.id}/get_surveys_index_txt_view/${custom_view_id || 0}/${survey_parent_id || 0}/download${queryStr ? '?' + queryStr : ''}`, {
          headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' }
        });
        startTxtPolling();
      } catch (err) { console.error('Export TXT error:', err); setCreatingTxt(false); }
    } else if (exportFormat === 'csv') {
      setTxtExportType('csv'); setCreatingTxt(true); setTxtWasCreated(false); setLastTxtUrl('');
      try {
        await fetch(`/formatos/format_categories/${format_category.id}/formats/${formato.id}/get_surveys_index_csv_view/${custom_view_id || 0}/${survey_parent_id || 0}/download${queryStr ? '?' + queryStr : ''}`, {
          headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' }
        });
        startTxtPolling();
      } catch (err) { console.error('Export CSV error:', err); setCreatingTxt(false); }
    }
  }, [formato, format_category, custom_view_id, survey_parent_id, buildExportParams]);

  function startExcelPolling() {
    let attempts = 0;
    if (excelPollRef.current) clearInterval(excelPollRef.current);
    excelPollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > 60) {
        clearInterval(excelPollRef.current);
        setCreatingExcel(false);
        return;
      }
      try {
        const res = await fetch(`/formatos/get_is_download_excel/${formato.id}?_=${Date.now()}`, {
          headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' },
          cache: 'no-store'
        });
        const data = await res.json();
        if (!data.creating_excel) {
          clearInterval(excelPollRef.current);
          setCreatingExcel(false);
          if (data.last_excel_url) {
            setExcelWasCreated(true);
            setLastExcelUrl(data.last_excel_url);
          }
        }
      } catch (err) {
        clearInterval(excelPollRef.current);
        setCreatingExcel(false);
      }
    }, 3000);
  }

  function startTxtPolling() {
    let attempts = 0;
    if (txtPollRef.current) clearInterval(txtPollRef.current);
    txtPollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > 60) {
        clearInterval(txtPollRef.current);
        setCreatingTxt(false);
        return;
      }
      try {
        const res = await fetch(`/formatos/get_is_download_txt/${formato.id}?_=${Date.now()}`, {
          headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' },
          cache: 'no-store'
        });
        const data = await res.json();
        if (!data.creating_txt) {
          clearInterval(txtPollRef.current);
          setCreatingTxt(false);
          if (data.last_txt_url) {
            setTxtWasCreated(true);
            setLastTxtUrl(data.last_txt_url);
          }
        }
      } catch (err) {
        clearInterval(txtPollRef.current);
        setCreatingTxt(false);
      }
    }, 3000);
  }

  // =========== Generate PDF (polling, like Excel) ===========

  function startPdfPolling() {
    let attempts = 0;
    if (pdfPollRef.current) clearInterval(pdfPollRef.current);
    pdfPollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > 120) {
        clearInterval(pdfPollRef.current);
        setCreatingPdf(false);
        setStatePDF('Error: el PDF tardó demasiado en generarse.');
        setTimeout(() => setStatePDF(''), 5000);
        return;
      }
      try {
        const res = await fetch(`/formatos/get_is_download_pdf/${formato.id}?_=${Date.now()}`, {
          headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' },
          cache: 'no-store'
        });
        const data = await res.json();
        if (!data.creating_pdf) {
          clearInterval(pdfPollRef.current);
          setCreatingPdf(false);
          setStatePDF('');
          if (data.last_pdf_url) {
            setPdfWasCreated(true);
            setLastPdfUrl(data.last_pdf_url);
          }
        }
      } catch (err) {
        clearInterval(pdfPollRef.current);
        setCreatingPdf(false);
        setStatePDF('Error al verificar estado del PDF.');
        setTimeout(() => setStatePDF(''), 5000);
      }
    }, 3000);
  }

  const handleSendPdf = useCallback(async (pdf) => {
    if (selectedIds.length === 0) {
      alert('Selecciona al menos un registro para generar el PDF.');
      return;
    }
    setCreatingPdf(true);
    setPdfWasCreated(false);
    setLastPdfUrl('');
    setStatePDF(`Generando PDF "${pdf.name}" (${selectedIds.length} registros)...`);
    try {
      await fetch(
        `/formatos/generate_pdf_all_download/${formato.id}?pdf_id=${pdf.id}`,
        {
          method: 'POST',
          headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' },
          body: JSON.stringify({ selected_items: selectedIds })
        }
      );
      startPdfPolling();
    } catch (err) {
      console.error('Generate PDF error:', err);
      setCreatingPdf(false);
      setStatePDF('Error al iniciar generación de PDF.');
      setTimeout(() => setStatePDF(''), 5000);
    }
  }, [selectedIds, formato.id]);

  // =========== Reporte de matrices por periodo (ZIP en background) ===========
  const handleMatrixReport = useCallback((report) => {
    if (selectedIds.length === 0) {
      alert('Selecciona al menos un registro para generar el reporte.');
      return;
    }
    setMatrixReport(report);
  }, [selectedIds]);

  // =========== Download PDF (email, old system) ===========

  const handleDownloadPdf = useCallback(async (pdf) => {
    if (selectedIds.length === 0) {
      alert('Selecciona al menos un registro para descargar el PDF.');
      return;
    }
    setStatePDF(`Enviando PDF "${pdf.name}" a tu correo...`);
    try {
      await fetch(
        `/formatos/send_pdf_all/${formato.id}/${custom_view_id || 0}/${survey_parent_id || 0}?pdf_id=${pdf.id}&view=${view || ''}`,
        {
          method: 'POST',
          headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' },
          body: JSON.stringify({ selected_items: selectedIds })
        }
      );
      setStatePDF(`PDF "${pdf.name}" enviado a tu correo.`);
      setTimeout(() => setStatePDF(''), 4000);
    } catch (err) {
      console.error('Download PDF error:', err);
      setStatePDF('Error al enviar PDF.');
      setTimeout(() => setStatePDF(''), 4000);
    }
  }, [selectedIds, formato.id, custom_view_id, survey_parent_id, view]);

  // =========== Bulk Update ===========

  const handleBulkUpdate = useCallback(async () => {
    if (selectedIds.length === 0) return;
    setIsUpdatingAll(true);
    setUpdateTime(0);
    setUpdateHasError(false);
    try {
      await fetch(
        `/formatos/update_surveys/${formato.id}/${survey_parent_id || 0}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ selected_items: selectedIds }),
          headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' }
        }
      );
      startUpdatePolling();
    } catch (err) {
      console.error('Bulk update error:', err);
      setIsUpdatingAll(false);
    }
  }, [selectedIds, formato.id, survey_parent_id]);

  function startUpdatePolling() {
    let seconds = 0;
    if (updatePollRef.current) clearInterval(updatePollRef.current);
    if (updateTimerRef.current) clearInterval(updateTimerRef.current);

    updateTimerRef.current = setInterval(() => {
      seconds++;
      setUpdateTime(seconds);
    }, 1000);

    updatePollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/formatos/get_is_updating_all/${formato.id}`, {
          headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' }
        });
        const data = await res.json();
        if (!data.is_updating_all) {
          clearInterval(updatePollRef.current);
          clearInterval(updateTimerRef.current);
          setIsUpdatingAll(false);
          if (data.update_has_error) {
            setUpdateHasError(true);
          } else {
            setSelectedIds([]);
            fetchData();
          }
        }
      } catch (err) {
        clearInterval(updatePollRef.current);
        clearInterval(updateTimerRef.current);
        setIsUpdatingAll(false);
      }
    }, 3000);
  }

  const handleDismissUpdateError = useCallback(async () => {
    try {
      await fetch(`/formatos/update_error_state/${formato.id}`, {
        method: 'PATCH',
        headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' }
      });
      setUpdateHasError(false);
    } catch (err) {
      console.error(err);
    }
  }, [formato.id]);

  // =========== Bulk Duplicate ===========

  const handleBulkDuplicate = useCallback(async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`¿Duplicar ${selectedIds.length} registro(s)? Se clonarán también sus registros hijos.`)) return;
    setIsDuplicating(true);
    setDuplicateHasError(false);
    try {
      const res = await fetch(
        `/formatos/duplicate_surveys/${formato.id}/${survey_parent_id || 0}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ selected_items: selectedIds }),
          headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' }
        }
      );
      if (res.status === 409) {
        // Ya hay una duplicación en curso: seguimos el estado existente.
        startDuplicatePolling();
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'No se pudo iniciar la duplicación.');
        setIsDuplicating(false);
        return;
      }
      startDuplicatePolling();
    } catch (err) {
      console.error('Bulk duplicate error:', err);
      setIsDuplicating(false);
    }
  }, [selectedIds, formato.id, survey_parent_id]);

  function startDuplicatePolling() {
    let attempts = 0;
    if (duplicatePollRef.current) clearInterval(duplicatePollRef.current);
    duplicatePollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > 300) {
        // Tope de seguridad (~15 min). El backend también auto-sana el flag;
        // aquí soltamos la UI para no dejar el spinner colgado indefinidamente.
        clearInterval(duplicatePollRef.current);
        setIsDuplicating(false);
        setDuplicateHasError(true);
        return;
      }
      try {
        const res = await fetch(`/formatos/get_is_duplicating/${formato.id}`, {
          headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' }
        });
        const data = await res.json();
        if (!data.is_duplicating) {
          clearInterval(duplicatePollRef.current);
          setIsDuplicating(false);
          if (data.has_error) {
            setDuplicateHasError(true);
          } else {
            setSelectedIds([]);
            fetchData();
          }
        }
      } catch (err) {
        clearInterval(duplicatePollRef.current);
        setIsDuplicating(false);
      }
    }, 3000);
  }

  // =========== Emails ===========

  const handleSendEmails = useCallback(async (surveyId) => {
    try {
      const res = await fetch(
        `/formatos/format_categories/${format_category.token}/formats/${formato.token}/surveys/${surveyId}/send_emails`,
        {
          method: 'POST',
          headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' }
        }
      );
      if (res.ok) {
        alert('Correos enviados correctamente.');
      }
    } catch (err) {
      console.error('Send emails error:', err);
    }
  }, [formato.token, format_category.token]);

  const handleOpenEmailConfirmations = useCallback((surveyId) => {
    setSelectedSurveyIdForConfirmations(surveyId);
    setShowEmailConfirmationsModal(true);
  }, []);

  // =========== Upload result ===========

  const handleUploadResult = useCallback((msg) => {
    setMsgUpload(msg);
    setMsgUploadShow(true);
  }, []);

  const handleCloseUploadMsg = useCallback(() => {
    setMsgUploadShow(false);
    setMsgUpload('');
  }, []);

  // =========== Refresh (from row actions) ===========
  const handleRefresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // =========== Taimes Extract ===========
  const handleTaimesExtract = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = null;
    setTaimesLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`/formatos/taimes_extract/${formato.id}`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': TOKEN },
        body: formData
      });
      const data = await res.json();
      setTaimesLoading(false);
      if (data.type === 'success') {
        alert(`Registro creado: ${data.mapped_fields} campos extraídos de ${data.extracted_fields} detectados.`);
        fetchData(true);
      } else {
        alert(`Error: ${data.error}`);
      }
    } catch (err) {
      setTaimesLoading(false);
      alert(`Error al procesar: ${err.message}`);
    }
  }, [formato.id, fetchData]);

  // =========== Format update time ===========
  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  const fcToken = format_category.token;

  // URL de eventos del calendario con los MISMOS filtros/búsqueda que la tabla,
  // para que el calendario respete la toolbar. Cambia cuando cambian los filtros
  // → FormatCalendar (embebido) recarga vía componentDidUpdate.
  const calendarDataUrl = (() => {
    const p = new URLSearchParams();
    if (custom_view_id) p.append('custom_view_id', custom_view_id);
    if (survey_parent_id && survey_parent_id != 0) p.append('survey_parent_id', survey_parent_id);
    if (appliedSearch) {
      p.append('search', appliedSearch);
      if (appliedSearchColumn) p.append('search_column', appliedSearchColumn);
    }
    Object.entries(filters).forEach(([qid, f]) => {
      if (f && f.value) {
        p.append(`filter[${qid}]`, f.value);
        if (f.operator) p.append(`op[${qid}]`, f.operator);
      }
    });
    const qs = p.toString();
    return `/formatos/records/${formato.id}/calendar_data${qs ? '?' + qs : ''}`;
  })();

  if (accessDenied) {
    return (
      <div className="cm-empty-state">
        <i className="fas fa-lock cm-empty-state__icon" />
        <p className="cm-empty-state__title">No tienes acceso a este formato</p>
        <p className="cm-empty-state__subtitle">Pide a un administrador el permiso "Ingresar" sobre este formato</p>
      </div>
    );
  }

  return (
    <>
      <RecordsPageHeader
        formato={formato}
        format_category={format_category}
        estados={estados}
        custom_view_id={custom_view_id}
        custom_view_name={custom_view_name}
        survey_parent_id={survey_parent_id}
        survey_parent_token={survey_parent_token}
        count_registers={meta.total != null ? meta.total : count_registers}
        current_plan={current_plan}
        tenant={tenant}
        onShowUpgradePlan={() => setShowUpgradePlanModal(true)}
      />

      <RecordsBreadcrumb formato={formato} format_category={format_category} breadcrumb={breadcrumb} custom_view_name={custom_view_name} />

      {hasTaimesIntegration && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 16px 8px' }}>
          <input
            type="file"
            ref={taimesFileInput}
            style={{ display: 'none' }}
            accept=".pdf,.png,.jpg,.jpeg,.tiff,.webp"
            onChange={handleTaimesExtract}
          />
          <button
            className="cm-btn cm-btn--sm"
            onClick={() => taimesFileInput.current.click()}
            disabled={taimesLoading}
          >
            {taimesLoading ? (
              <><i className="fas fa-spinner fa-spin" /> Extrayendo...</>
            ) : (
              <><i className="fas fa-robot" /> Extraer con Taimes</>
            )}
          </button>
        </div>
      )}

      {/* ===== Banners ===== */}
      {planLimitMsg && !showUpgradePlanModal && (
        <div className="cm-banner cm-banner--info">
          <i className="fas fa-info-circle" /> {planLimitMsg}
          <button className="cm-banner__dismiss" onClick={() => setPlanLimitMsg('')}>&times;</button>
        </div>
      )}

      {isUpdatingAll && (
        <div className="cm-banner cm-banner--primary">
          <span className="cm-spinner" /> Actualizando registros... Tiempo: {formatTime(updateTime)}
        </div>
      )}

      {updateHasError && (
        <div className="cm-banner cm-banner--danger">
          <i className="fas fa-exclamation-triangle" /> Error al actualizar registros.
          <button className="cm-banner__dismiss" onClick={handleDismissUpdateError}>&times;</button>
        </div>
      )}

      {isDuplicating && (
        <div className="cm-banner cm-banner--primary">
          <span className="cm-spinner" /> Duplicando registros...
        </div>
      )}

      {duplicateHasError && (
        <div className="cm-banner cm-banner--danger">
          <i className="fas fa-exclamation-triangle" /> Error al duplicar registros.
          <button className="cm-banner__dismiss" onClick={() => setDuplicateHasError(false)}>&times;</button>
        </div>
      )}

      {creatingExcel && (
        <div className="cm-banner cm-banner--info">
          <span className="cm-spinner" /> Generando archivo Excel...
        </div>
      )}

      {excelWasCreated && lastExcelUrl && (
        <div className="cm-banner cm-banner--success">
          <i className="fas fa-check-circle" /> Excel generado.{' '}
          <a href={lastExcelUrl} target="_blank" rel="noopener noreferrer">Descargar archivo</a>
          <button className="cm-banner__dismiss" onClick={() => setExcelWasCreated(false)}>&times;</button>
        </div>
      )}

      {creatingTxt && (
        <div className="cm-banner cm-banner--info">
          <span className="cm-spinner" /> Generando archivo {txtExportType.toUpperCase()}...
        </div>
      )}

      {txtWasCreated && lastTxtUrl && (
        <div className="cm-banner cm-banner--success">
          <i className="fas fa-check-circle" /> {txtExportType.toUpperCase()} generado.{' '}
          <a href={lastTxtUrl} target="_blank" rel="noopener noreferrer">Descargar archivo</a>
          <button className="cm-banner__dismiss" onClick={() => setTxtWasCreated(false)}>&times;</button>
        </div>
      )}

      {creatingPdf && statePDF && (
        <div className="cm-banner cm-banner--info">
          <span className="cm-spinner" /> {statePDF}
        </div>
      )}

      {pdfWasCreated && lastPdfUrl && (
        <div className="cm-banner cm-banner--success">
          <i className="fas fa-check-circle" /> PDF generado.{' '}
          <a href={lastPdfUrl} target="_blank" rel="noopener noreferrer">Descargar PDF</a>
          <button className="cm-banner__dismiss" onClick={() => setPdfWasCreated(false)}>&times;</button>
        </div>
      )}

      {statePDF && !creatingPdf && (
        <div className={`cm-banner ${statePDF.includes('Error') ? 'cm-banner--danger' : 'cm-banner--info'}`}>
          {statePDF}
        </div>
      )}

      {msgUploadShow && msgUpload && (
        <div className="cm-banner cm-banner--info">
          <i className="fas fa-info-circle" /> {msgUpload}
          <button className="cm-banner__dismiss" onClick={handleCloseUploadMsg}>&times;</button>
        </div>
      )}

      <div className="cm-dt">
        <RecordsInlineToolbar
          formato={formato}
          format_category={format_category}
          estados={estados}
          format_calendar_enabled={format_calendar_enabled}
          selectedIds={selectedIds}
          onBulkDelete={handleBulkDelete}
          onBulkViewChange={handleBulkViewChange}
          count_registers={meta.total != null ? meta.total : count_registers}
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters(f => !f)}
          onClearFilters={handleClearFilters}
          onCancelFilters={handleCancelFilters}
          columns={columns}
          searchText={searchText}
          searchColumn={searchColumn}
          onSearchTextChange={setSearchText}
          onSearchColumnChange={setSearchColumn}
          onSearch={(clearSearch) => { setAppliedSearch(clearSearch === '' ? '' : searchText); setAppliedSearchColumn(clearSearch === '' ? '' : searchColumn); setPage(1); cursorRef.current = { lastId: null, lastSurveyCount: null }; }}
          domain={domain}
          tenant={tenant}
          format_pdfs={format_pdfs}
          format_custom_view_dashboard={format_custom_view_dashboard}
          configuration_format={configuration_format}
          custom_view_id={custom_view_id}
          survey_parent_id={survey_parent_id}
          onCopyLink={handleCopyLink}
          onShowQr={() => setShowQrModal(true)}
          showColumnOptions={showColumnOptions}
          onToggleColumnOptions={() => setShowColumnOptions(v => !v)}
          onExportExcel={() => setShowExcelColumnSelector(true)}
          onShowUploadModal={() => setShowUploadModal(true)}
          onSendPdf={handleSendPdf}
          onDownloadPdf={handleDownloadPdf}
          matrix_reports={matrix_reports}
          onMatrixReport={handleMatrixReport}
          onBulkUpdate={handleBulkUpdate}
          isUpdatingAll={isUpdatingAll}
          view={view}
          viewMode={viewMode}
          onToggleView={() => setViewMode(m => (m === 'calendar' ? 'table' : 'calendar'))}
        />

        {viewMode === 'calendar' ? (
          <FormatCalendar
            embedded={true}
            formato={formato}
            format_category={format_category}
            dataUrl={calendarDataUrl}
            records_url={`/formatos/records/${formato.id}`}
            domain={domain}
          />
        ) : (
        <>
        {selectedIds.length > 0 && (
          <div className="cm-bulk-bar">
            <span className="cm-bulk-bar__count">{selectedIds.length} seleccionados</span>
            {estados.edit && (
              <button
                className="cm-btn cm-btn--sm cm-btn--primary"
                onClick={handleBulkUpdate}
                disabled={isUpdatingAll}
              >
                <i className="fas fa-sync-alt" /> Actualizar
              </button>
            )}
            {estados.create && (
              <button
                className="cm-btn cm-btn--sm"
                onClick={handleBulkDuplicate}
                disabled={isDuplicating}
              >
                <i className="fas fa-copy" /> Duplicar
              </button>
            )}
            {/* <button className="cm-btn cm-btn--sm" onClick={() => setSelectedIds([])}>
              <i className="fas fa-times" /> Deseleccionar
            </button> */}
            {estados.delete && (
              <button className="cm-btn cm-btn--danger cm-btn--sm" onClick={handleBulkDelete}>
                <i className="fas fa-trash" /> Eliminar
              </button>
            )}
          </div>
        )}

        <RecordsTable
          columns={columns}
          rows={rows}
          loading={loading}
          sortField={sortField}
          sortDir={sortDir}
          onSort={handleSort}
          showFilters={showFilters}
          filters={pendingFilters}
          hasActiveFilters={Object.values(filters).some(f => f && f.value)}
          onFilterChange={handlePendingFilterChange}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          selectedIds={selectedIds}
          onSelect={handleSelect}
          onSelectAll={handleSelectAll}
          editingCell={editingCell}
          savingCell={savingCell}
          onStartEdit={(surveyId, questionId) => { if (estados.edit) setEditingCell({ surveyId, questionId }); }}
          onCancelEdit={() => setEditingCell(null)}
          onSaveEdit={handleCellSave}
          formato={formato}
          format_category={format_category}
          estados={estados}
          users={users}
          procesos={procesos}
          positions={positions}
          domain={domain}
          custom_view_id={custom_view_id}
          configuration_format={configuration_format}
          onSendEmails={handleSendEmails}
          onViewConfirmations={handleOpenEmailConfirmations}
          onRefresh={handleRefresh}
          froalaConfig={{ key: key_text, imageUpload }}
          showColumnOptions={showColumnOptions}
        />

        <RecordsPagination
          page={meta.page || page}
          total={meta.total || 0}
          perPage={meta.per_page || perPage}
          onPageChange={handlePageChange}
          onPerPageChange={(val) => {
            setPerPage(val);
            setPage(1);
            cursorRef.current = { lastId: null, lastSurveyCount: null };
            // Persist to DB
            if (configuration_format?.id) {
              fetch(`/formatos/configuration_formats/${configuration_format.id}`, {
                method: 'PATCH',
                body: JSON.stringify({ quantity_to_show: val }),
                headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' }
              }).catch(() => {});
            }
          }}
        />
        </>
        )}
      </div>

      {/* ===== Modals ===== */}
      {showExcelColumnSelector && (
        <ExcelColumnSelector
          columns={columns}
          onExport={handleExportFromSelector}
          onClose={() => setShowExcelColumnSelector(false)}
        />
      )}

      {showUploadModal && (
        <UploadFile
          modal={showUploadModal}
          toggle={() => setShowUploadModal(false)}
          title="Importar desde Excel"
          format_category={format_category}
          format={formato}
          survey_parent_id={survey_parent_id || 0}
          loadData={fetchData}
          msgUpload={handleUploadResult}
          closeMsgUpload={handleCloseUploadMsg}
        />
      )}

      {matrixReport && (
        <ModalMatrixReport
          modal={!!matrixReport}
          toggle={() => setMatrixReport(null)}
          report={matrixReport}
          selectedIds={selectedIds}
        />
      )}

      {showQrModal && (
        <ModalQr
          modal={showQrModal}
          toggle={() => setShowQrModal(false)}
          url={survey_parent_id && parseInt(survey_parent_id) > 0 && survey_parent_token
            ? `${domain}/formatos/generate_questions_surveys/${formato.token}/${survey_parent_token}/parent`
            : `${domain}/formatos/generate_questions/${formato.token}/0/${custom_view_id && parseInt(custom_view_id) > 0 ? custom_view_id : 0}`}
          backdrop="static"
        />
      )}

      {showEmailConfirmationsModal && (
        <ModalEmailConfirmations
          modal={showEmailConfirmationsModal}
          toggle={() => setShowEmailConfirmationsModal(false)}
          surveyId={selectedSurveyIdForConfirmations}
        />
      )}

      {showUpgradePlanModal && (
        <FormUpgradePlan
          modal={showUpgradePlanModal}
          toggle={() => setShowUpgradePlanModal(false)}
          toogle={() => setShowUpgradePlanModal(false)}
          title="Actualizar plan"
          messageModal="Has alcanzado el límite de tu plan actual."
          plans={plans}
          current_plan={current_plan}
          backdrop="static"
        />
      )}
    </>
  );
}
